import { conversionFactors } from "../conversionFactors";
import { stockingPredatorRowIsBatch } from "../../../../utils/stockingPredatorMath";

/** @param {unknown} date */
function toYyyyMmDd(date) {
  if (date == null || date === "") return null;
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    return date.slice(0, 10);
  }
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function sampleDateKeyFromStockingDate(date) {
  return toYyyyMmDd(date) || "unknown";
}

/**
 * @param {object} params
 * @param {Array<object>} params.events
 * @param {boolean} params.useAllData
 * @param {string|null} params.startYmd - YYYY-MM-DD
 * @param {string|null} params.endYmd
 * @param {string|null} params.singleSessionYmd - pond detail session day (single-URL mode)
 */
export function filterStockingEventsByGraphRange({
  events,
  useAllData,
  startYmd,
  endYmd,
  singleSessionYmd,
}) {
  if (!Array.isArray(events) || events.length === 0) return [];
  if (singleSessionYmd) {
    return events.filter((ev) => toYyyyMmDd(ev.StockingDate) === singleSessionYmd);
  }
  if (useAllData) return events;
  if (!startYmd) return [];
  return events.filter((ev) => {
    const d = toYyyyMmDd(ev.StockingDate);
    if (!d) return false;
    if (endYmd) return d >= startYmd && d <= endYmd;
    return d === startYmd;
  });
}

function normalizeLengthUnitKey(name) {
  const s = (name || "").toString().toLowerCase();
  if (s.includes("mm") || s.includes("milli")) return "mm";
  return "inch";
}

function normalizeWeightUnitKey(name) {
  const s = (name || "").toString().toLowerCase();
  if (s.includes("gram")) return "gram";
  return "pound";
}

function getConversionsFromStockingEvent(event, selectedUnit) {
  const lName = event?.lengthUnit?.Name ?? event?.lengthUnit?.name;
  const wName = event?.weightUnit?.Name ?? event?.weightUnit?.name;
  const pondLengthUnit = normalizeLengthUnitKey(lName);
  const pondWeightUnit = normalizeWeightUnitKey(wName);
  const lengthMap = conversionFactors.length[pondLengthUnit];
  const weightMap = conversionFactors.weight[pondWeightUnit];
  const lengthConversion =
    lengthMap?.[selectedUnit.value] ?? conversionFactors.length.inch[selectedUnit.value];
  const weightConversion =
    weightMap?.[selectedUnit.value] ?? conversionFactors.weight.pound[selectedUnit.value];
  const convertLength = (value) => parseFloat(value || 0) * lengthConversion;
  const convertWeight = (value) => parseFloat(value || 0) * weightConversion;
  return { convertLength, convertWeight };
}

export function predatorSpeciesNameFromStockingRow(row) {
  return (
    row?.fishType?.Name ||
    row?.FishTypeName ||
    row?.fishType?.name ||
    row?.FishType?.Name ||
    ""
  );
}

/**
 * Predator species label from API row; resolves FishTypeID via dropdown/catalog when name missing.
 * @param {object} row
 * @param {Array<{ label: string, value: string|number }>} [fishTypeOptions]
 * @param {Array<{ id?: number, Name?: string, FishTypeName?: string }>} [fishTypesCatalog]
 */
export function resolveStockingPredatorSpeciesLabel(
  row,
  fishTypeOptions = [],
  fishTypesCatalog = []
) {
  const fromRow = predatorSpeciesNameFromStockingRow(row);
  if (fromRow && String(fromRow).trim()) return String(fromRow).trim();

  const id = row?.FishTypeID ?? row?.fishTypeId ?? row?.FishTypeId;
  if (id == null || id === "") return "";

  const idStr = String(id);
  const opt = (fishTypeOptions || []).find(
    (o) => o?.value != null && String(o.value) === idStr
  );
  if (opt?.label) return String(opt.label).trim();

  const ft = (fishTypesCatalog || []).find(
    (t) => t?.id != null && String(t.id) === idStr
  );
  if (ft) {
    const n = ft.Name ?? ft.FishTypeName ?? ft.name;
    if (n != null && String(n).trim()) return String(n).trim();
  }
  return "";
}

function predatorSpeciesName(row, fishTypeOptions, fishTypesCatalog) {
  const name = resolveStockingPredatorSpeciesLabel(
    row,
    fishTypeOptions,
    fishTypesCatalog
  );
  return name || "Unknown";
}

function matchesSpeciesFilter(speciesName, selectedFish) {
  if (!selectedFish || selectedFish.length === 0) return true;
  return selectedFish.some((f) => f.label === speciesName);
}

function preySpeciesName(row, fishTypeOptions, fishTypesCatalog) {
  const fromPredPath = resolveStockingPredatorSpeciesLabel(
    row,
    fishTypeOptions,
    fishTypesCatalog
  );
  if (fromPredPath && String(fromPredPath).trim()) return String(fromPredPath).trim();
  const forage =
    (row?.ForageTypeName != null && String(row.ForageTypeName).trim()) ||
    (row?.forageTypeName != null && String(row.forageTypeName).trim()) ||
    "";
  return forage || "Unknown";
}

function parsePreyStockingHeadcount(row) {
  const fromTag = row?.Tag != null ? String(row.Tag).trim() : "";
  const fromNumber =
    row?.Number !== undefined && row?.Number !== null ? String(row.Number).trim() : "";
  const raw = fromTag || fromNumber;
  const digits = raw.replace(/,/g, "");
  const n = digits === "" ? NaN : parseInt(digits, 10);
  if (!Number.isNaN(n) && String(n) === digits) return Math.max(0, n);
  return null;
}

/**
 * Unique predator species names from stocking events (all predator rows, including batch).
 * Also includes forage/prey species that match the fish-type filter (stocked Bluegill, etc.).
 * @param {Array<object>} events
 * @param {Array<{ label: string }>} [selectedFish]
 * @returns {string[]}
 */
export function getStockedPredatorSpeciesLabelsFromEvents(
  events,
  selectedFish = [],
  fishTypeOptions = [],
  fishTypesCatalog = []
) {
  const names = new Set();
  if (!Array.isArray(events)) return [];
  for (const event of events) {
    const rows = event.predatorRows || event.PredatorRows || [];
    for (const row of rows) {
      const species = predatorSpeciesName(row, fishTypeOptions, fishTypesCatalog);
      if (!species || species === "Unknown") continue;
      if (!matchesSpeciesFilter(species, selectedFish)) continue;
      names.add(species);
    }
    const prey = event.preyRows || event.PreyRows || [];
    for (const row of prey) {
      const species = preySpeciesName(row, fishTypeOptions, fishTypesCatalog);
      if (!species || species === "Unknown") continue;
      if (!matchesSpeciesFilter(species, selectedFish)) continue;
      names.add(species);
    }
  }
  return [...names].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

/**
 * Collapse per-fish stocking rows into one point per species + chart date (mean Wr, sample stdev, count).
 * @param {Array<object>} rows
 */
function aggregateStockingRowsForAvgWrVsDate(rows) {
  const byKey = new Map();
  for (const row of rows) {
    const species = row.species || "Unknown";
    const dateKey = row.date;
    const key = `${species}\0${dateKey}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(row);
  }
  const out = [];
  for (const [, group] of byKey) {
    const wrs = group.map((r) => Number(r.averageWr)).filter((v) => Number.isFinite(v));
    const n = wrs.length;
    if (n === 0) continue;
    const mean = wrs.reduce((a, b) => a + b, 0) / n;
    let stdev = 0;
    if (n > 1) {
      const variance = wrs.reduce((s, x) => s + (x - mean) ** 2, 0) / (n - 1);
      stdev = Math.sqrt(variance);
    }
    const base = group[0];
    out.push({
      ...base,
      averageWr: parseFloat(mean.toFixed(2)),
      stdev: parseFloat(stdev.toFixed(2)),
      count: n,
    });
  }
  return out;
}

/**
 * @param {Array<object>} events - filtered stocking events from API
 * @param {string} graphType
 * @param {{ selectedFish: Array<{label:string}>, selectedUnit: {value:string}, formatDateTime?: (d: unknown) => string }} ctx
 */
export function buildStockingPredatorGraphRows(events, graphType, ctx) {
  const {
    selectedFish,
    selectedUnit,
    formatDateTime,
    fishTypeOptions = [],
    fishTypesCatalog = [],
  } = ctx;
  if (!Array.isArray(events) || events.length === 0) return [];

  const rows = [];

  events.forEach((event) => {
    const { convertLength, convertWeight } = getConversionsFromStockingEvent(
      event,
      selectedUnit
    );
    const sampleDateKey = sampleDateKeyFromStockingDate(event.StockingDate);
    const sampleDateLabel = formatDateTime
      ? formatDateTime(event.StockingDate)
      : String(event.StockingDate ?? "N/A");

    (event.predatorRows || []).forEach((row) => {
      if (stockingPredatorRowIsBatch(row)) return;
      const species = predatorSpeciesName(row, fishTypeOptions, fishTypesCatalog);
      if (!matchesSpeciesFilter(species, selectedFish)) return;

      const normalizedTag = (row.Tag != null ? String(row.Tag) : "").trim();
      const base = {
        species,
        fishTypeName: species,
        tagNumber: normalizedTag || null,
        isTagged: !!normalizedTag,
        isStocking: true,
        stockingEventId: event.id,
        stockingDate: event.StockingDate,
      };

      const len = parseFloat(row.Length);
      const wt = parseFloat(row.Weight);
      const ws = parseFloat(row.Ws);
      const wr = parseFloat(row.Wr);

      if (graphType === "wr-vs-length") {
        if (!Number.isFinite(len)) return;
        rows.push({
          ...base,
          length: convertLength(len),
          relativeWeight: Number.isFinite(wr) ? wr : 0,
        });
        return;
      }

      if (graphType === "weight-vs-length" || graphType === "standard-vs-actual") {
        if (!(len > 0 && wt > 0)) return;
        rows.push({
          ...base,
          length: convertLength(len),
          actualWeight: convertWeight(wt),
          standardWeight: convertWeight(Number.isFinite(ws) ? ws : 0),
        });
        return;
      }

      if (graphType === "weight-vs-length-by-date") {
        if (!(len > 0 && wt > 0)) return;
        rows.push({
          ...base,
          length: convertLength(len),
          actualWeight: convertWeight(wt),
          standardWeight: convertWeight(Number.isFinite(ws) ? ws : 0),
          sampleDateKey,
          sampleDateLabel,
        });
        return;
      }

      if (graphType === "avg-wr-vs-date") {
        if (!Number.isFinite(wr)) return;
        rows.push({
          ...base,
          date: formatDateTime ? formatDateTime(event.StockingDate) : String(event.StockingDate),
          averageWr: parseFloat(wr.toFixed(2)),
          stdev: 0,
          count: 1,
        });
        return;
      }

      if (graphType === "tagged-wr-vs-date") {
        if (!Number.isFinite(wr)) return;
        if (!normalizedTag) return;
        const date = formatDateTime
          ? formatDateTime(event.StockingDate)
          : String(event.StockingDate ?? "N/A");
        rows.push({
          ...base,
          date,
          wr: parseFloat(wr.toFixed(4)),
          seriesKey: `${species} · Tag ${normalizedTag}`,
        });
        return;
      }

      if (graphType === "number-frequency" || graphType === "number-frequency-vs-bin") {
        const originalLength = parseFloat(row.Length || 0);
        if (!Number.isFinite(originalLength) || originalLength <= 0) return;
        rows.push({
          ...base,
          length: convertLength(originalLength),
          originalLength,
          relativeWeight: Number.isFinite(wr) ? wr : 0,
          status: "Stocking",
        });
        return;
      }

      if (graphType === "number-frequency-vs-bin-by-date") {
        const originalLength = parseFloat(row.Length || 0);
        if (!Number.isFinite(originalLength) || originalLength <= 0) return;
        rows.push({
          ...base,
          length: convertLength(originalLength),
          originalLength,
          relativeWeight: Number.isFinite(wr) ? wr : 0,
          status: "Stocking",
          sampleDateKey,
          sampleDateLabel,
        });
      }
    });

    // Forage / prey stocking (e.g. Bluegill batch) — include when species filter matches.
    (event.preyRows || event.PreyRows || []).forEach((row) => {
      const species = preySpeciesName(row, fishTypeOptions, fishTypesCatalog);
      if (!matchesSpeciesFilter(species, selectedFish)) return;

      const base = {
        species,
        fishTypeName: species,
        tagNumber: null,
        isTagged: false,
        isStocking: true,
        stockingEventId: event.id,
        stockingDate: event.StockingDate,
      };

      const lenIndiv = parseFloat(row.Length);
      const minLen = parseFloat(row.MinLength ?? row.minLength);
      const avgLen = Number.isFinite(lenIndiv)
        ? lenIndiv
        : Number.isFinite(minLen)
          ? minLen
          : NaN;
      const wt = parseFloat(row.Weight ?? row.TotalWeight);
      const wr = parseFloat(row.Wr);
      const ws = parseFloat(row.Ws);
      const head = parsePreyStockingHeadcount(row);

      if (graphType === "wr-vs-length") {
        if (!Number.isFinite(avgLen) || !Number.isFinite(wr)) return;
        rows.push({
          ...base,
          length: convertLength(avgLen),
          relativeWeight: wr,
        });
        return;
      }

      if (graphType === "weight-vs-length" || graphType === "standard-vs-actual") {
        if (!(avgLen > 0 && wt > 0)) return;
        rows.push({
          ...base,
          length: convertLength(avgLen),
          actualWeight: convertWeight(wt),
          standardWeight: convertWeight(Number.isFinite(ws) ? ws : 0),
        });
        return;
      }

      if (graphType === "weight-vs-length-by-date") {
        if (!(avgLen > 0 && wt > 0)) return;
        rows.push({
          ...base,
          length: convertLength(avgLen),
          actualWeight: convertWeight(wt),
          standardWeight: convertWeight(Number.isFinite(ws) ? ws : 0),
          sampleDateKey,
          sampleDateLabel,
        });
        return;
      }

      if (graphType === "avg-wr-vs-date") {
        if (!Number.isFinite(wr)) return;
        rows.push({
          ...base,
          date: formatDateTime ? formatDateTime(event.StockingDate) : String(event.StockingDate),
          averageWr: parseFloat(wr.toFixed(2)),
          stdev: 0,
          count: head != null && head > 0 ? head : 1,
        });
        return;
      }

      if (graphType === "number-frequency" || graphType === "number-frequency-vs-bin") {
        if (!Number.isFinite(avgLen) || avgLen <= 0) return;
        const count = head != null && head > 0 ? head : 1;
        rows.push({
          ...base,
          length: convertLength(avgLen),
          originalLength: avgLen,
          relativeWeight: Number.isFinite(wr) ? wr : NaN,
          status: "Stocking",
          stockingHeadcount: count,
        });
        return;
      }

      if (graphType === "number-frequency-vs-bin-by-date") {
        if (!Number.isFinite(avgLen) || avgLen <= 0) return;
        const count = head != null && head > 0 ? head : 1;
        rows.push({
          ...base,
          length: convertLength(avgLen),
          originalLength: avgLen,
          relativeWeight: Number.isFinite(wr) ? wr : NaN,
          status: "Stocking",
          stockingHeadcount: count,
          sampleDateKey,
          sampleDateLabel,
        });
      }
    });
  });

  if (graphType === "avg-wr-vs-date" && rows.length > 0) {
    return aggregateStockingRowsForAvgWrVsDate(rows);
  }
  return rows;
}

/**
 * Stocking events already filtered by pond/date range; narrow to one sampling session calendar day.
 * @param {Array<object>} events
 * @param {unknown} sessionDate - pond detail session date (same shape as detail.Date)
 */
export function filterStockingEventsBySessionDay(events, sessionDate) {
  const ymd = toYyyyMmDd(sessionDate);
  if (!ymd || !Array.isArray(events)) return [];
  return events.filter((ev) => toYyyyMmDd(ev.StockingDate) === ymd);
}

/**
 * Flatten predator rows for table/PDF/Excel (one row per API predator row; Number not used).
 * @param {Array<object>} events - filtered stocking events
 * @param {{ selectedFish: Array<{label:string}>, selectedUnit: {value:string}, formatDateTime?: (d: unknown) => string }} ctx
 */
export function buildStockingPredatorTableRows(events, ctx) {
  const { selectedFish, selectedUnit, formatDateTime } = ctx;
  if (!Array.isArray(events) || events.length === 0) return [];

  const rows = [];
  events.forEach((event) => {
    const { convertLength, convertWeight } = getConversionsFromStockingEvent(
      event,
      selectedUnit
    );
    const stockingDateLabel = formatDateTime
      ? formatDateTime(event.StockingDate)
      : String(event.StockingDate ?? "N/A");

    (event.predatorRows || []).forEach((row, predIndex) => {
      if (stockingPredatorRowIsBatch(row)) return;
      const species = predatorSpeciesName(row);
      if (!matchesSpeciesFilter(species, selectedFish)) return;

      const len = parseFloat(row.Length);
      const wt = parseFloat(row.Weight);
      const ws = parseFloat(row.Ws);
      const wr = parseFloat(row.Wr);
      const normalizedTag = (row.Tag != null ? String(row.Tag) : "").trim();
      const notesRaw = event.Notes ?? event.notes ?? "";
      const notes = notesRaw != null ? String(notesRaw) : "";

      rows.push({
        id: `${event.id ?? "ev"}-${predIndex}`,
        stockingDate: event.StockingDate,
        stockingDateLabel,
        species,
        length: Number.isFinite(len) ? convertLength(len) : null,
        weight: Number.isFinite(wt) ? convertWeight(wt) : null,
        ws: Number.isFinite(ws) ? convertWeight(ws) : null,
        wr: Number.isFinite(wr) ? wr : null,
        tag: normalizedTag || "N/A",
        notes,
        captureMethod: "Stocking",
      });
    });
  });

  return rows;
}
